/**
 * Die Werkzeugkette — dreizehn Stationen, und was davon steht.
 *
 * WARUM DAS HIER LIEGT UND NICHT IM FLIESSTEXT. Eine Kette, die als Aufsatz
 * beschrieben ist, veraltet zwischen zwei Sätzen: eine Station wird gebaut, der
 * Text sagt weiter „geplant". Hier stehen sie als DATEN, und der Zustand jeder
 * Station steht neben ihr. Wer eine baut, ändert eine Zeile — und die Seite
 * sagt es.
 *
 * WAS DIESE DATEI NICHT TUT: sie prüft nichts nach. `status` ist eine Angabe
 * und keine Messung; niemand kann von hier aus feststellen, ob ein Abonnement
 * läuft oder eine Grafikkarte steht. Was der Rechenkern selbst weiss — die
 * Zahlen des Modells, die Achsen, die Massen — steht auf den Seiten daneben
 * und wird hier nicht wiederholt.
 *
 * STAND DER ANGABEN: August 2026, abgeglichen mit der Wegaufnahme. Zwei Dinge
 * haben sich gegenüber der ersten Fassung verschoben und sind hier so
 * eingetragen:
 *
 *   – Newton ist seit März 2026 fertig und liest MJCF unmittelbar. Damit ist
 *     die Datei, die aus diesem Simulator fällt, nicht mehr das Eingangsformat
 *     eines Nebenwerkzeugs, sondern das der Trainingsstrecke selbst.
 *   – MuJoCo ist keine zweite Physik mehr — der Standardlöser von Newton IST
 *     MuJoCo. Station 6 bleibt trotzdem, mit einer anderen und besseren
 *     Begründung: sie ist die kurze Schleife und die zweite RICHTUNG.
 */

/**
 * Was von einer Station in diesem Repository steht.
 *
 * `gebaut` heisst: läuft, hat Tests, ist über die Oberfläche erreichbar.
 * `platzhalter` heisst: die Schnittstelle steht, es kommen aber keine echten
 * Daten. `fremd` heisst: fertige Software, die niemand hier baut — sie muss
 * beschafft, installiert oder bezahlt werden. `offen` heisst: noch nichts.
 */
export type StationState = 'gebaut' | 'platzhalter' | 'fremd' | 'offen'

export interface Station {
  /** Nummer in der Kette; 0 ist der Master. */
  no: number
  name: string
  /** Was sie beiträgt — in einem Satz. */
  purpose: string
  /** Was sie ausdrücklich NICHT tut. Der häufigere Irrtum. */
  notThis: string
  licence: string
  /** Richtwert, netto. `null` heisst: kostet kein Geld, nur Zeit. */
  cost: string | null
  state: StationState
  /** Wo sie in dieser Anwendung liegt — nur bei `gebaut` und `platzhalter`. */
  where?: string
  /** Was der nächste Schritt wäre. `null`, wenn sie fertig ist. */
  next: string | null
}

export const STATIONS: readonly Station[] = [
  {
    no: 0,
    name: 'HUMANOID SIM',
    purpose:
      'Der Master. Punktsatz, Achsen, Freiheitsgrade, Grenzen, Momente, Referenzbewegungen — der einzige Ort, an dem Kinematik geschrieben wird.',
    notThis:
      'Er trainiert nichts und rechnet keine Physik. Er liefert die Zahlen, gegen die alle anderen arbeiten.',
    licence: 'eigen',
    cost: null,
    state: 'gebaut',
    where: 'diese Anwendung',
    next: null,
  },
  {
    no: 1,
    name: 'CAD',
    purpose:
      'Bauteile und Baugruppen. Liefert die Zahlen, die der Master nicht selbst kennt: Massen, Schwerpunkte, Trägheiten, Netze.',
    notThis:
      'Es bekommt Punkte und gibt Skalare zurück, nie umgekehrt. Eine Kinematik, die im CAD entsteht, ist die zweite.',
    licence: 'Abo je Platz',
    cost: '1.500–8.000 €/Jahr',
    state: 'fremd',
    next: 'Platz einrichten und den Rückweg über das PLM festlegen.',
  },
  {
    no: 2,
    name: 'PLM',
    purpose:
      'Die Verwaltung dazwischen: Stände, Freigaben, Antriebsdaten, und die Antwort auf die Frage, welcher Datensatz in welchen Lauf ging.',
    notThis: 'Es hält keine zweite Kinematik.',
    licence: 'eigen',
    cost: null,
    state: 'platzhalter',
    where: 'Kachel PLM · Stückliste',
    next:
      'Es liefert heute 0 von 25 Positionen. Der Weg steht, die Daten fehlen. Die Frage „welcher Datensatz ging in welchen Lauf" beantwortet inzwischen die Modellkennung: sie steht in jeder erzeugten Datei.',
  },
  {
    no: 3,
    name: 'Isaac Sim 6.0',
    purpose:
      'Der Simulator: liest die Beschreibung, wandelt nach USD, stellt Szene, Umgebungsgeometrie und Sensorik bereit.',
    notThis: 'Er trainiert nicht. Er stellt die Welt bereit, in der trainiert wird.',
    licence: 'Apache 2.0',
    cost: null,
    state: 'fremd',
    next: 'Braucht eine Grafikkarte. Das MJCF und das URDF liegen bereit.',
  },
  {
    no: 4,
    name: 'Newton 1.0',
    purpose:
      'Die Physik darunter: Kontakt, Reibung, Dynamik, geschlossene Ketten. Liest MJCF, URDF und USD unmittelbar.',
    notThis:
      'Man bedient sie nicht direkt — sie entscheidet trotzdem über die Glaubwürdigkeit jedes Ergebnisses.',
    licence: 'quelloffen · Linux Foundation',
    cost: null,
    state: 'fremd',
    next: 'Sie liest genau die Datei, die der Export liefert. Es fehlt die Hardware, nicht das Modell.',
  },
  {
    no: 5,
    name: 'Isaac Lab 3.0',
    purpose:
      'Das eigentliche Training: Aufgabe, Belohnung, Streuung, tausende Instanzen parallel. Hier fällt der Laufregler.',
    notThis:
      'Es liefert keine Maschine. Die Ingenieursarbeit dieser Station ist die Belohnung, nicht das Modell.',
    licence: 'BSD-3-Clause',
    cost: null,
    state: 'fremd',
    next: 'Die Aufgabe ist geschrieben (`handoff/train.py`): Beobachtung, Aktion, Belohnung, Streuung. Die Anbindung an Isaac Lab ist UNGEPRÜFT — sie ist auf keiner Grafikkarte gelaufen.',
  },
  {
    no: 6,
    name: 'MuJoCo',
    purpose:
      'Die kurze Schleife: Sekunden statt Minuten, am Arbeitsplatz und im Browser, mit derselben Datei wie die grosse Strecke.',
    notThis:
      'Keine zweite Physik mehr — der Standardlöser von Newton ist MuJoCo. Der Wert liegt in der zweiten RICHTUNG: vorwärts gerechnet gegen die rückwärts gerechnete Gangvorgabe.',
    licence: 'Apache 2.0',
    cost: null,
    state: 'gebaut',
    where: 'Kachel Prüfstand',
    next: null,
  },
  {
    no: 7,
    name: 'Isaac Lab-Arena',
    purpose:
      'Abnahme: gleiche Aufgabe, gleiche Messlatte, vergleichbare Zahlen über mehrere Reglerstände.',
    notThis: 'Ohne feste Messlatte entsteht Fortschritt nach Augenschein.',
    licence: 'quelloffen',
    cost: null,
    state: 'fremd',
    next: 'Setzt Station 5 voraus.',
  },
  {
    no: 8,
    name: 'Cosmos',
    purpose: 'Bild- und Videosynthese zur Datenvermehrung. Speist Station 9.',
    notThis: 'Nur nötig, wenn die Maschine sehen soll. Für einen Gang nicht.',
    licence: 'offene Modelllizenz',
    cost: 'Rechenzeit',
    state: 'offen',
    next: 'Eigene Baustelle. Solange Gen5 nur laufen soll, kann die Kette bei 7 enden.',
  },
  {
    no: 9,
    name: 'LeRobot',
    purpose:
      'Datensatzformat. Demonstrationen und Bewegungsdaten kommen hier in die Form, die Station 10 erwartet.',
    notThis:
      'Kein Programm, ein Format. Es entscheidet nur, ob unsere Daten lesbar sind — steht seit lerobot 0.4.0 bei v3.0: viele Episoden je Datei statt einer je Episode.',
    licence: 'Apache 2.0',
    cost: null,
    state: 'gebaut',
    where: 'Kachel Werkzeugkette · Übergabe',
    next: 'Der Simulator liefert `motion.json`, `handoff/to_lerobot.py` macht daraus den Datensatz im Format v3.0 — mit gerechneten Kennwerten je Merkmal, die der Regler zum Normieren braucht. Braucht pandas und pyarrow, keine Grafikkarte.',
  },
  {
    no: 10,
    name: 'Isaac GR00T N1.7',
    purpose:
      'Die Aufgabenebene: versteht eine Anweisung und gibt Bewegungsziele aus. Setzt auf dem Laufregler auf.',
    notThis: 'Es lernt keinen Gang.',
    licence: 'offene Modelllizenz, kommerziell erlaubt',
    cost: 'Rechenzeit · 40 GB Grafikspeicher zum Nachtrainieren',
    state: 'fremd',
    next: 'Beides liegt bereit: der Datensatz aus Station 9 und `modality.json` mit der Kanalbeschreibung. Es fehlt die Grafikkarte — 40 GB zum Nachtrainieren.',
  },
  {
    no: 11,
    name: 'Laufzeitumgebung',
    purpose: 'Wandelt den trainierten Regler in die Form, die auf kleiner Hardware schnell läuft.',
    notThis: 'Sie ändert nichts am Regler, nur seine Gestalt.',
    licence: 'herstellereigen, kostenfrei · Alternative MIT',
    cost: null,
    state: 'offen',
    next: 'Setzt Station 5 voraus.',
  },
  {
    no: 12,
    name: 'Bordrechner + ROS 2',
    purpose:
      'Der Einsatz auf der Maschine, mit derselben Beschreibung als Grundlage. Hier zeigt sich, ob die Kette gestimmt hat.',
    notThis: 'Er korrigiert nichts, was davor falsch war.',
    licence: 'Apache 2.0 · Gerät gekauft',
    cost: '3.500–5.500 USD je Stück',
    state: 'offen',
    next: 'Setzt Station 11 voraus.',
  },
]

/**
 * Die drei Einschnitte — Übergänge, die etwas ändern.
 *
 * Nicht jeder Schritt ist gleich. Diese drei sind Zäsuren, und wer sie
 * übersieht, merkt es erst danach.
 */
export interface Cut {
  after: number
  what: string
}

export const CUTS: readonly Cut[] = [
  {
    after: 2,
    what: 'Der Master verlässt das Haus. Ab hier gibt es nur noch erzeugte Kopien — jede Handänderung an einer Ausgabe erzeugt ein zweites Modell.',
  },
  {
    after: 7,
    what: 'Der Gang steht und ist abgenommen. Alles davor ist Pflicht für Gen5.',
  },
  {
    after: 8,
    what: 'Beginn einer eigenen Baustelle. Solange Gen5 nur laufen soll, kann die Kette hier enden.',
  },
]

/** Wie viele Stationen in einem Zustand sind. */
export const countState = (state: StationState): number =>
  STATIONS.filter((s) => s.state === state).length

/**
 * Was die Kette tatsächlich an Lizenzgeld kostet.
 *
 * ZWEI VON DREIZEHN. Die ganze Lernstrecke ist frei; bezahlt werden das CAD,
 * die Rechner und die Zeit. Das Unternehmens-Abonnement wird nur fällig, wenn
 * die Simulationsumgebung selbst an Dritte geht oder als Dienst betrieben wird
 * — Ergebnisse lösen es nicht aus.
 */
export const PAID = STATIONS.filter((s) => s.cost !== null)

/* -------------------------------------------------------------------------
   Installation — was auf die Maschine mit der Grafikkarte kommt
   ------------------------------------------------------------------------- */

/**
 * Ein Schritt der Einrichtung.
 *
 * `verify` ist der wichtigste Teil und der, den Anleitungen üblicherweise
 * weglassen: ein Befehl, dessen AUSGABE zeigt, dass der Schritt geklappt hat.
 * Ohne ihn merkt man einen halb gelungenen Schritt erst drei Schritte später,
 * an einem Fehler, der wie etwas anderes aussieht.
 */
export interface InstallStep {
  what: string
  /** Wörtlich, zum Kopieren. */
  cmd: string
  verify?: string
  /** Was hier schiefgeht. */
  note?: string
}

/**
 * Wie sicher die Angabe ist.
 *
 * DAS GEHÖRT AN JEDEN BEFEHL. Ein abgeschriebener Befehl aus der
 * Dokumentation und einer, der aus der Erinnerung stammt, sehen gleich aus und
 * sind es nicht. Wer das nicht trennt, sucht den Fehler später an der falschen
 * Stelle — genau die Unterscheidung, die auch im Kopf von `handoff/train.py`
 * steht.
 */
export type Confidence = 'dokumentiert' | 'abgeleitet'

export interface Install {
  /** Zu welcher Station der Kette. */
  station: number
  title: string
  /** Warum dieser Schritt überhaupt nötig ist. */
  why: string
  confidence: Confidence
  /** Woher die Befehle stammen. */
  source: string
  steps: InstallStep[]
}

/**
 * Was vor allem anderen stehen muss.
 *
 * DIE REIHENFOLGE IST NICHT BELIEBIG. Wer Isaac Sim vor dem Treiber
 * installiert, bekommt eine Fehlermeldung über fehlende Erweiterungen und
 * sucht sie im falschen Paket.
 */
export const PREREQ: readonly InstallStep[] = [
  {
    what: 'Linux. Isaac Sim und Isaac Lab laufen auf Ubuntu 22.04 oder 24.04; Windows ist möglich, aber der Weg dorthin ist länger.',
    cmd: 'lsb_release -d',
    verify: 'Ubuntu 22.04 oder 24.04',
  },
  {
    what: 'NVIDIA-Treiber und CUDA. Ohne sie startet nichts von Station 3 bis 5.',
    cmd: 'nvidia-smi',
    verify: 'Zeigt Karte, Treiberfassung und freien Speicher',
    note: 'Reicht der Speicher nicht, scheitert das Training später und nicht hier — die Zahl in der rechten oberen Ecke ist die, auf die es ankommt.',
  },
  {
    what: 'Python 3.12. Isaac Sim 6.x verlangt genau diese Fassung.',
    cmd: 'python3.12 --version',
    verify: 'Python 3.12.x',
    note: 'Nicht 3.11 und nicht 3.13. Eine andere Fassung führt zu einem Auflösungsfehler bei der Installation, der nach einem Netzproblem aussieht.',
  },
  {
    what: '`uv` als Paketverwalter. Die Isaac-Dokumentation empfiehlt es ausdrücklich; es ist deutlich schneller als pip und conda.',
    cmd: 'curl -LsSf https://astral.sh/uv/install.sh | sh',
    verify: 'uv --version',
  },
]

export const INSTALLS: readonly Install[] = [
  {
    station: 6,
    title: 'MuJoCo — der kurze Weg zuerst',
    why: 'Bevor irgendetwas trainiert wird, muss das Modell stehen. MuJoCo braucht keine Grafikkarte, startet in Sekunden und zeigt in einem Fenster, ob der Roboter zusammenhält. Wer diesen Schritt überspringt, sucht einen Modellfehler später in einem Trainingslauf.',
    confidence: 'dokumentiert',
    source: 'pypi.org/project/mujoco — dieselbe Fassung läuft in der Kachel Prüfstand',
    steps: [
      {
        what: 'MuJoCo installieren',
        cmd: 'uv pip install mujoco',
        verify: 'python -c "import mujoco; print(mujoco.__version__)"',
      },
      {
        what: 'Das erzeugte Modell ansehen',
        cmd: 'python -m mujoco.viewer --mjcf gen5.xml',
        verify: 'Ein Fenster mit dem Roboter. Er fällt um — ohne Gleichgewichtsregler ist das richtig.',
        note: 'Bricht es mit einer Meldung ab, steht der Fehler in der ersten Zeile und nennt den Körper. Das Bündel kommt aus der Kachel Werkzeugkette.',
      },
    ],
  },
  {
    station: 9,
    title: 'LeRobot — das Datensatzformat',
    why: 'Kein Programm, ein Format. Es entscheidet, ob unsere Bewegungsdaten für Station 10 lesbar sind. Braucht keine Grafikkarte und lässt sich deshalb neben allem anderen erledigen.',
    confidence: 'dokumentiert',
    source: 'huggingface/lerobot — Datensatzformat v3.0 ab lerobot 0.4.0',
    steps: [
      {
        what: 'LeRobot installieren',
        cmd: 'uv pip install "lerobot>=0.4.0" pandas pyarrow',
        verify: 'python -c "import lerobot; print(lerobot.__version__)"',
        note: 'Das Datensatzformat v3.0 gibt es erst ab 0.4.0. Ältere Fassungen schreiben v2.0 mit einer Datei je Episode.',
      },
      {
        what: 'Die Referenzbewegung umsetzen',
        cmd: 'python handoff/to_lerobot.py motion.json --out datasets/hs2-walk',
        verify: 'Meldet Episode, Bilder, Achsen und Bildrate',
      },
      {
        what: 'Gegenprobe: den Datensatz zurücklesen',
        cmd: 'python -c "from lerobot.datasets.lerobot_dataset import LeRobotDataset; d=LeRobotDataset(\'datasets/hs2-walk\'); print(len(d), d.features.keys())"',
        verify: 'Anzahl Bilder und die Merkmalsnamen',
        note: 'DIESER SCHRITT IST DER EIGENTLICHE TEST. Ein Datensatz, den der Schreiber für richtig hält und der Leser nicht öffnet, ist keiner.',
      },
    ],
  },
  {
    station: 3,
    title: 'Isaac Sim 6.0 und Isaac Lab 3.0',
    why: 'Der Simulator stellt die Welt bereit, in der trainiert wird, die Trainingsschicht die Aufgabe. Beide kommen aus demselben Paket.',
    confidence: 'dokumentiert',
    source: 'isaac-sim.github.io/IsaacLab — Installation über Pip-Pakete',
    steps: [
      {
        what: 'Eigene Umgebung mit Python 3.12',
        cmd: 'uv venv --python 3.12 --seed env_isaaclab && source env_isaaclab/bin/activate',
        verify: 'python --version',
      },
      {
        what: 'Isaac Lab mit Isaac Sim',
        cmd: 'uv pip install "isaaclab[isaacsim]==3.0.0"',
        verify: 'python -c "import isaaclab; print(isaaclab.__version__)"',
        note: 'Ohne feste Fassung zieht es die neueste — für einen Stand, auf den sich jemand berufen soll, ist das die falsche Wahl.',
      },
      {
        what: 'Erster Start',
        cmd: 'isaacsim',
        verify: 'Ein Fenster. Der erste Start holt alle Erweiterungen aus der Registrierung.',
        note: 'DAUERT ZEHN MINUTEN UND SIEHT AUS WIE EIN HÄNGER. Nicht abbrechen — ein abgebrochener erster Start hinterlässt eine halbe Installation.',
      },
    ],
  },
  {
    station: 4,
    title: 'Newton 1.0 als Physik',
    why: 'Newton liest unser MJCF unmittelbar. Damit rechnet die Trainingsstrecke mit derselben Datei wie der Prüfstand im Browser — und nicht mit einer zweiten Fassung desselben Roboters.',
    confidence: 'dokumentiert',
    source: 'newton-physics/newton · Linux Foundation. Newton steckt in den Isaac-Lab-Erweiterungen bereits mit drin.',
    steps: [
      {
        what: 'Newton einzeln, falls es nicht schon aus Isaac Lab kam',
        cmd: 'uv pip install "newton[examples]"',
        verify: 'python -c "import newton; print(newton.__version__)"',
        note: 'Das Paket heisst auf PyPI `newton`, nicht `newton-physics`. Es setzt auf NVIDIA Warp auf und bringt MuJoCo Warp mit — deshalb ist der Standardlöser derselbe Kern wie in der Kachel Prüfstand.',
      },
      {
        what: 'Die Anbindung in Isaac Lab liegt auf dem Zweig `develop`',
        cmd: 'git clone -b develop https://github.com/isaac-sim/IsaacLab.git',
        verify: 'Der Ordner source/isaaclab/isaaclab/newton existiert',
        note: 'Die Newton-Anbindung ist Teil von Isaac Lab 3.0 Beta und wird aktiv geändert. Wer sie benutzt, sollte den Stand notieren, gegen den er gebaut hat.',
      },
    ],
  },
  {
    station: 10,
    title: 'Isaac GR00T N1.7',
    why: 'Die Aufgabenebene. Sie lernt keinen Gang — sie setzt auf dem Laufregler auf. Gen5 ist für sie eine NEUE BAUFORM, und das ist der Weg, den die Anleitung dafür beschreibt.',
    confidence: 'dokumentiert',
    source: 'github.com/NVIDIA/Isaac-GR00T — getting_started/finetune_new_embodiment.md',
    steps: [
      {
        what: 'Quelltext mit Untermodulen holen',
        cmd: 'git clone --recurse-submodules https://github.com/NVIDIA/Isaac-GR00T && cd Isaac-GR00T',
        note: 'Ohne `--recurse-submodules` fehlen Teile, und der Fehler kommt erst beim Import.',
      },
      {
        what: 'Abhängigkeiten',
        cmd: 'bash scripts/deployment/dgpu/install_deps.sh',
        verify: 'python -c "import gr00t; print(gr00t.__version__)"',
      },
      {
        what: 'Nachtrainieren auf Gen5 als neue Bauform',
        cmd: 'python scripts/launch_finetune.py --dataset-path ../datasets/hs2-walk --embodiment-tag new_embodiment',
        note: '40 GB Grafikspeicher zum Nachtrainieren, 16 GB zum Ausführen. Die Kanalbeschreibung der neuen Bauform kommt aus `meta/info.json` unseres Datensatzes — dort steht unter `neura.joints`, was jede Spalte bedeutet.',
      },
    ],
  },
  {
    station: 5,
    title: 'Das Training selbst',
    why: 'Hier fällt der Laufregler. Die Aufgabe steht in `handoff/train.py`; die Anbindung an Isaac Lab ist der Teil, der auf dieser Maschine gegenzuprüfen ist.',
    confidence: 'abgeleitet',
    source: 'Eigenes Skript. Die AUFGABE ist geprüft, die ANBINDUNG nicht — sie ist auf keiner Grafikkarte gelaufen.',
    steps: [
      {
        what: 'Erst die Aufgabe prüfen, ohne Isaac Lab und ohne Karte',
        cmd: 'python handoff/train.py --model gen5.xml --motion motion.json --dry-run',
        verify: 'Achszahl, Zyklusdauer und die Achsen ohne Vorbild',
        note: 'Stimmen die Zahlen hier nicht mit denen der Kachel Prüfstand überein, ist das Bündel veraltet. Neu herunterladen, bevor eine Nacht Rechenzeit hineingeht.',
      },
      {
        what: 'Dann die Anbindung gegenprüfen',
        cmd: '$EDITOR handoff/train.py',
        note: 'Die Namen der Konfigurationsklassen haben sich zwischen den Fassungen von Isaac Lab mehrfach geändert. Was im Skript steht, ist gegen die Dokumentation geschrieben und nicht gegen eine laufende Installation.',
      },
    ],
  },
]

/** Die Einrichtung einer Station, falls es eine gibt. */
export const installFor = (station: number): Install | undefined =>
  INSTALLS.find((i) => i.station === station)
